import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconMailPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { qk } from '@/api/queries';
import type { StaffRole } from '@/api/types';
import { useAuth } from '@/auth/context';
import { can, ROLE_LABEL } from '@/auth/permissions';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { formatDateTime } from '@/lib/format';

const ROLES: { value: StaffRole; label: string }[] = [
  { value: 'editor', label: 'Editor — create and edit, submit for review' },
  { value: 'approver', label: 'Approver — also approve, publish, archive' },
  { value: 'admin', label: 'Admin — also manage staff' },
];

export function StaffPage() {
  const { staff: me } = useAuth();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: qk.staff,
    queryFn: adminApi.listStaff,
    enabled: can(me?.role, 'manageStaff'),
  });
  const [inviteOpen, inviteModal] = useDisclosure();
  const onError = (e: unknown) =>
    notifications.show({ color: 'coral', title: 'Not changed', message: errorMessage(e) });
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; role?: StaffRole; disabled?: boolean }) =>
      adminApi.updateStaff(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.staff });
      notifications.show({ color: 'leaf', message: 'Saved.' });
    },
    onError,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => adminApi.revokeInvite(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.staff });
      notifications.show({ color: 'leaf', message: 'Invite revoked.' });
    },
    onError,
  });

  if (!can(me?.role, 'manageStaff'))
    return (
      <EmptyState title="Admins only">
        Ask an admin if you need to invite someone or change a role.
      </EmptyState>
    );

  return (
    <>
      <PageHeader
        title="Staff"
        description="Who can use the admin, and what they can do."
        actions={
          <Button leftSection={<IconMailPlus size={16} />} onClick={inviteModal.open}>
            Invite
          </Button>
        }
      />
      <QueryState query={query}>
        {(data) => (
          <Stack gap="xl">
            <Table.ScrollContainer minWidth={640}>
              <Table striped aria-label="Staff">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Role</Table.Th>
                    <Table.Th>Access</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.users.map((u) => {
                    const self = u.id === me?.id;
                    return (
                      <Table.Tr key={u.id}>
                        <Table.Td>
                          <Group gap={6} wrap="nowrap">
                            <Text size="sm" fw={500}>
                              {u.name}
                            </Text>
                            {self && (
                              <Badge size="xs" variant="outline" color="gray">
                                You
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>{u.email}</Table.Td>
                        <Table.Td>
                          <Tooltip label="You can’t change your own role" disabled={!self}>
                            <Select
                              aria-label={`Role for ${u.name}`}
                              size="xs"
                              w={140}
                              allowDeselect={false}
                              disabled={self}
                              value={u.role}
                              data={ROLES.map((r) => ({
                                value: r.value,
                                label: ROLE_LABEL[r.value],
                              }))}
                              onChange={(v) =>
                                v &&
                                v !== u.role &&
                                update.mutate({ id: u.id, role: v as StaffRole })
                              }
                            />
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <Switch
                            aria-label={`${u.name} can sign in`}
                            label={u.disabled ? 'Disabled' : 'Active'}
                            disabled={self}
                            checked={!u.disabled}
                            onChange={(e) => {
                              const disable = !e.currentTarget.checked;
                              if (!disable) return update.mutate({ id: u.id, disabled: false });
                              modals.openConfirmModal({
                                title: `Disable ${u.name}?`,
                                children: (
                                  <Text size="sm">
                                    They’re signed out straight away and can’t sign in until
                                    re-enabled.
                                  </Text>
                                ),
                                labels: { confirm: 'Disable', cancel: 'Cancel' },
                                confirmProps: { color: 'coral' },
                                onConfirm: () => update.mutate({ id: u.id, disabled: true }),
                              });
                            }}
                          />
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <Stack gap="xs">
              <Title order={2} size="h3">
                Pending invites
              </Title>
              {data.invites.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No pending invites.
                </Text>
              ) : (
                <Table.ScrollContainer minWidth={520}>
                  <Table aria-label="Pending invites">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Role</Table.Th>
                        <Table.Th>Expires</Table.Th>
                        <Table.Th w={60} />
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {data.invites.map((i) => (
                        <Table.Tr key={i.id}>
                          <Table.Td>{i.email}</Table.Td>
                          <Table.Td>{ROLE_LABEL[i.role]}</Table.Td>
                          <Table.Td>{formatDateTime(i.expires_at)}</Table.Td>
                          <Table.Td>
                            <Tooltip label="Revoke invite">
                              <ActionIcon
                                variant="subtle"
                                color="coral"
                                aria-label={`Revoke invite for ${i.email}`}
                                onClick={() => revoke.mutate(i.id)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </Stack>
          </Stack>
        )}
      </QueryState>
      <InviteModal opened={inviteOpen} onClose={inviteModal.close} />
    </>
  );
}

function InviteModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const form = useForm({
    initialValues: { email: '', role: 'editor' as StaffRole },
    validate: { email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Enter an email address.') },
  });
  const invite = useMutation({
    mutationFn: (v: typeof form.values) =>
      adminApi.inviteStaff({ email: v.email.trim(), role: v.role }),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: qk.staff });
      notifications.show({
        color: 'leaf',
        message: `Invite sent to ${v.email}. The link lasts 72 hours.`,
      });
      form.reset();
      onClose();
    },
  });
  return (
    <Modal opened={opened} onClose={onClose} title="Invite someone">
      <form onSubmit={form.onSubmit((v) => invite.mutate(v))}>
        <Stack>
          <TextInput
            label="Email"
            type="email"
            required
            data-autofocus
            {...form.getInputProps('email')}
          />
          <Select label="Role" allowDeselect={false} data={ROLES} {...form.getInputProps('role')} />
          {invite.error && (
            <Text size="sm" c="coral.8" role="alert">
              {errorMessage(invite.error)}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={invite.isPending}>
              Send invite
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
